import type { AxiosError } from "axios";
import { useHistory, useParams } from "react-router-dom";
import useSWR from "swr";
import {
  GoBackButtonSmall,
  InvisibleButton,
  PrimaryButtonMedium,
} from "../../../components/Buttons/Buttons";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { endpoints } from "../../../endpoints";
import type { PIMProduct } from "../../../types/types.PIM";
import {
  ErrorWithStatus,
  useStoreState,
  makeUrlWithParams,
} from "../../../util/util";
import { PIMProductDetailsPortfolio } from "../../public/ProductDetailsPortfolio/PIMProductDetailsPortfolio";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useRoutePath } from "../../../util/Routing";
import { ErrorPlaceholder } from "../../../components/Error";
import { CenterContainer } from "../../../layout/publicPageLayout";
import { H4, Title } from "../../../components/Typography/Typography";
import { FullwidthPageWrapper } from "../../../components/ProductsLayout/SharedProductsLayout";

export const BuyerProductDetailPortfolio = () => {
  const { productSlug: productId } = useParams<{ productSlug: string }>();
  const [, setbackToProductList] = useState(false);
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const { t } = useTranslation();

  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { tenant_id, storefront_metadata } = useStoreState();

  const {
    data: product,
    error: productError,
    mutate: mutateProduct,
  } = useSWR<PIMProduct, AxiosError>(
    productId
      ? makeUrlWithParams(
          endpoints.v2_tenants_id_pim_products_id(tenant_id, productId),
          { use_database: "true" }
        )
      : null,
    {
      revalidateOnFocus: false,
    }
  );

  const errorWithStatus = productError
    ? new ErrorWithStatus(
        Number(productError?.code ?? 0),
        productError?.message ?? ""
      )
    : undefined;

  const onBackClick = () => {
    history.push(
      `${adminPath}/pim/products${
        sessionStorage.getItem("products_search") ?? ""
      }`
    );
  };

  if (!product && !productError) {
    return <DelayedSpinner />;
  }

  if (productError) {
    return (
      <>
        {productError.response?.data.message ===
        "Product is no longer available" ? (
          <>
            <InvisibleButton onClick={() => onBackClick()}>
              <GoBackButtonSmall text={t("Back")} />
            </InvisibleButton>

            <CenterContainer>
              <Title>{t("This product is not available")}</Title>
              <H4>
                {t(
                  "{{tenantName}} has not made this product publicly accessible. please contact {{tenantName}} to access this product",
                  {
                    tenantName: storefront_metadata.tenant_name,
                  }
                )}
              </H4>
              <PrimaryButtonMedium
                style={{ marginTop: "20px" }}
                onClick={() => history.push(`/store/${tenantSlug}`)}
              >
                {t("Go to Homepage")}
              </PrimaryButtonMedium>
            </CenterContainer>
          </>
        ) : productError.response?.data.message === "product not found!" ? (
          <>
            <InvisibleButton onClick={() => onBackClick()}>
              <GoBackButtonSmall text={t("Back")} />
            </InvisibleButton>

            <CenterContainer>
              <Title>{t("404")}</Title>
              <H4>{t("Product not found!")}</H4>
              <PrimaryButtonMedium
                style={{ marginTop: "20px" }}
                onClick={() => onBackClick()}
              >
                {t("Back")}
              </PrimaryButtonMedium>
            </CenterContainer>
          </>
        ) : (
          <>
            <InvisibleButton onClick={() => onBackClick()}>
              <GoBackButtonSmall text={t("Back")} />
            </InvisibleButton>
            <ErrorPlaceholder
              message={t("There was an error loading the product.")}
            />
          </>
        )}
      </>
    );
  }

  return (
    <FullwidthPageWrapper>
      <PIMProductDetailsPortfolio
        product={product}
        error={errorWithStatus}
        mutateProduct={mutateProduct}
        setbackToProductList={setbackToProductList}
        can_set_tab_query={false}
      />
    </FullwidthPageWrapper>
  );
};
